<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import type {
    Project,
    ProjectsByStatus,
    ProjectStatus,
    CalendarEvent,
    WeekPeriod,
    DisplayFilter,
  } from '../lib/types';
  import { showProjects, showEvents } from '../lib/types';
  import {
    getWeekDisplayRange,
    getWeekStart,
    getWeekEnd,
    isCurrentWeek,
    isFutureWeek,
    isPastWeek,
    getWeekNumber,
    categoryIcons,
    statusAccentColors,
    eventsForWeek,
    isMultiDayEvent,
    eventTotalDays,
    parseCalendarDate,
    progressStyle,
    progressComplete,
    sortEventsForDisplay,
    formatEventTimeShort,
  } from '../lib/utils';
  import { showProjectTooltip } from '../lib/stores/projectTooltip';
  import {
    openProjectRail,
    openProjectModalForCreate,
    requestProjectMove,
    openEventModal,
    openEventModalForEdit,
  } from '../lib/stores/ui';
  import { useExpandableProjects } from '../lib/useExpandableProjects.svelte';
  import { useProjectDragDrop } from '../lib/useProjectDragDrop.svelte';
  import ExpandableProjectChip from './ExpandableProjectChip.svelte';

  interface Props {
    visibleWeeks: string[];
    projectsByWeek: Record<string, ProjectsByStatus>;
    events: CalendarEvent[];
    currentWeek: string;
    period: WeekPeriod;
    display?: DisplayFilter | null;
  }

  let {
    visibleWeeks,
    projectsByWeek,
    events,
    currentWeek,
    period = 'month',
    display = null,
  }: Props = $props();

  // Get 5 weeks centered around current week (or use all visible weeks)
  let displayWeeks = $derived(() => {
    // For rolling view, show all visible weeks from the controller
    return visibleWeeks;
  });

  // Period options
  const periodOptions: { value: WeekPeriod; label: string }[] = [
    { value: '1-week', label: '1 Week' },
    { value: 'month', label: 'Month' },
    { value: '5', label: '5 weeks' },
    { value: '7', label: '7 weeks' },
    { value: 'quarter', label: 'Quarter' },
  ];

  function setPeriod(newPeriod: WeekPeriod) {
    if (newPeriod === period) return;

    const params = new URLSearchParams();
    params.set('view', 'rolling');
    if (newPeriod !== 'month') {
      params.set('range', newPeriod);
    }

    router.get(`/week/${currentWeek}?${params.toString()}`, {}, { preserveScroll: true });
  }

  // Get projects by status for a week
  function getProjects(weekKey: string, status: ProjectStatus): Project[] {
    const data = projectsByWeek[weekKey] || { planned: [], active: [], almost: [], done: [] };
    return data[status] || [];
  }

  // Get all projects for a week
  function getAllProjects(weekKey: string): Project[] {
    const data = projectsByWeek[weekKey] || { planned: [], active: [], almost: [], done: [] };
    return [...(data.active || []), ...(data.planned || []), ...(data.done || [])];
  }

  // Get project counts
  function getProjectCounts(weekKey: string): {
    planned: number;
    active: number;
    done: number;
    total: number;
  } {
    const data = projectsByWeek[weekKey] || { planned: [], active: [], almost: [], done: [] };
    return {
      planned: data.planned?.length || 0,
      active: data.active?.length || 0,
      done: data.done?.length || 0,
      total: (data.planned?.length || 0) + (data.active?.length || 0) + (data.done?.length || 0),
    };
  }

  // Get events for a specific week
  function getWeekEvents(weekKey: string): CalendarEvent[] {
    return sortEventsForDisplay(eventsForWeek(events, weekKey));
  }

  // Format event date with abbreviated weekday (e.g., "Mo 15")
  const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  function formatEventDateWithWeekday(dateStr: string): string {
    const date = parseCalendarDate(dateStr);
    const weekday = weekdays[date.getUTCDay()];
    const day = date.getUTCDate();
    return `${weekday} ${day}`;
  }

  // Week state
  function getWeekState(weekKey: string): 'past' | 'current' | 'future' {
    if (isCurrentWeek(weekKey)) return 'current';
    if (isPastWeek(weekKey)) return 'past';
    return 'future';
  }

  // Navigate to week focus view
  function navigateToWeek(weekKey: string) {
    router.get(`/week/${weekKey}?view=week-focus`, {}, { preserveScroll: true });
  }

  // Get the first day (Sunday) of a week for target_date
  function getWeekFirstDate(weekKey: string): string {
    const weekStart = getWeekStart(weekKey);
    return weekStart.toISOString().split('T')[0];
  }

  // Get month key from week key for project creation
  function getWeekMonthKey(weekKey: string): string {
    const weekStart = getWeekStart(weekKey);
    return weekStart.toISOString().slice(0, 7);
  }

  function handleAddProject(weekKey: string) {
    const monthKey = getWeekMonthKey(weekKey);
    const targetDate = getWeekFirstDate(weekKey);
    openProjectModalForCreate({
      schedulingState: 'scheduled',
      monthKey: monthKey,
      targetDate: targetDate,
      status: 'planned',
    });
  }

  // Expand/collapse state (shared utility)
  const expander = useExpandableProjects(() => getAllProjects(currentWeek));
  // eslint-disable-next-line momentium/no-unguarded-effect -- idempotent expander init: runs once via hasInitialized guard, needs reactive project data
  $effect(() => expander.initializeExpansion());

  // Drag and drop state (shared utility)
  const dnd = useProjectDragDrop((projectId, project, weekKey) => {
    requestProjectMove(projectId, project, 'week', { weekKey, status: 'planned' });
  });
</script>

<div class="rolling-container">
  <!-- Header with period selector -->
  <div class="rolling-header">
    <span class="header-title">Rolling Weeks</span>
    <div class="period-selector">
      {#each periodOptions as opt}
        <button
          type="button"
          class="period-btn"
          class:active={period === opt.value}
          onclick={() => setPeriod(opt.value)}
        >
          {opt.label}
        </button>
      {/each}
    </div>
  </div>

  <!-- Week cards stack -->
  <div class="weeks-stack">
    {#each displayWeeks() as weekKey, idx (weekKey)}
      {@const state = getWeekState(weekKey)}
      {@const isCurrent = state === 'current'}
      {@const isPast = state === 'past'}
      {@const isFuture = state === 'future'}
      {@const projects = getAllProjects(weekKey)}
      {@const counts = getProjectCounts(weekKey)}
      {@const weekEvents = getWeekEvents(weekKey)}
      {@const allExpanded = expander.areAllExpanded(projects)}
      {@const hasExpandable = projects.length > 0}

      <div
        class="week-card"
        class:past={isPast}
        class:current={isCurrent}
        class:future={isFuture}
        class:drop-target={dnd.dropTarget === weekKey}
        role="group"
        ondragover={(e) => dnd.handleDragOver(e, weekKey)}
        ondragleave={dnd.handleDragLeave}
        ondrop={(e) => dnd.handleDrop(e, weekKey)}
      >
        <!-- Week header -->
        <div class="week-header">
          <button type="button" class="week-title-btn" onclick={() => navigateToWeek(weekKey)}>
            <span class="week-range">{getWeekDisplayRange(weekKey)}</span>
            <span class="week-number">W{getWeekNumber(weekKey)}</span>
            {#if isCurrent}
              <span class="current-badge">This Week</span>
            {/if}
          </button>

          {#if hasExpandable}
            <button
              type="button"
              class="expand-toggle-btn"
              class:all-expanded={allExpanded}
              onclick={() =>
                allExpanded
                  ? expander.collapseGroup(getAllProjects(weekKey))
                  : expander.expandGroup(getAllProjects(weekKey))}
            >
              {#if allExpanded}
                <span class="toggle-icon">▼</span>
                <span class="toggle-text">Collapse</span>
              {:else}
                <span class="toggle-icon">▶</span>
                <span class="toggle-text">Expand</span>
              {/if}
            </button>
          {/if}

          <button
            type="button"
            class="add-btn"
            onclick={() => handleAddProject(weekKey)}
            title="Add project to this week"
          >
            +
          </button>
        </div>

        <!-- Week content -->
        <div class="week-content">
          <!-- Projects grid -->
          {#if showProjects(display)}
            <div class="projects-area">
              {#if projects.length > 0}
                <div class="projects-grid" class:expanded={isCurrent}>
                  {#each projects as project (project.id)}
                    <ExpandableProjectChip
                      {project}
                      expanded={expander.isProjectExpanded(project.id)}
                      onToggleExpand={() => expander.toggleProjectExpand(project.id)}
                      isDragging={dnd.draggingProjectId === project.id}
                      onDragStart={(e) => dnd.handleDragStart(e, project, weekKey)}
                      onDragEnd={dnd.handleDragEnd}
                    />
                  {/each}
                </div>
              {:else}
                <div class="empty-week">
                  {isFuture ? 'No targets planned' : 'No projects'}
                </div>
              {/if}
            </div>
          {/if}

          <!-- Events sidebar -->
          {#if showEvents(display)}
            <div class="events-sidebar">
              <div class="events-header">
                <span>📅 Events ({weekEvents.length})</span>
                <button
                  type="button"
                  class="events-add-btn"
                  onclick={() => openEventModal(getWeekFirstDate(weekKey))}
                  title="Add event"
                >
                  +
                </button>
              </div>
              {#if weekEvents.length > 0}
                <div class="events-list">
                  {#each weekEvents as event (event.id)}
                    {@const timeStr = formatEventTimeShort(event)}
                    <button
                      type="button"
                      class="event-item event-{event.type}"
                      onclick={() => openEventModalForEdit(event)}
                      title="Click to edit"
                    >
                      <span class="event-date"
                        >{formatEventDateWithWeekday(event.date)}{#if timeStr}
                          · {timeStr}{/if}</span
                      >
                      <span class="event-title">{event.title}</span>
                      {#if isMultiDayEvent(event)}
                        <span class="event-span">{eventTotalDays(event)}d</span>
                      {/if}
                    </button>
                  {/each}
                </div>
              {:else}
                <div class="events-empty">No events this week</div>
              {/if}
            </div>
          {/if}
        </div>
      </div>
    {/each}
  </div>
</div>

<style>
  .rolling-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0.5rem;
    gap: 0.5rem;
    overflow: hidden;
    background: #f9fafb;
  }

  /* Header */
  .rolling-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem 0.5rem;
    background: white;
    border-radius: 0.375rem;
    flex-shrink: 0;
  }

  .header-title {
    font-size: 0.75rem;
    font-weight: 700;
    color: #374151;
  }

  .period-selector {
    display: flex;
    background: #f9fafb;
    border-radius: 0.25rem;
    padding: 0.125rem;
    border: 1px solid #e5e7eb;
  }

  .period-btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.625rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .period-btn:hover {
    color: #374151;
    background: white;
  }

  .period-btn.active {
    color: #1d4ed8;
    background: white;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  /* Weeks stack */
  .weeks-stack {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    overflow-y: auto;
    padding-right: 0.25rem;
  }

  .week-card {
    background: white;
    border-radius: 0.375rem;
    overflow: hidden;
    transition: all 0.15s;
    flex-shrink: 0;
  }

  .week-card.past {
    background: #fafafa;
    box-shadow: inset 0 0 0 1px #e5e7eb;
  }

  .week-card.current {
    background: white;
    box-shadow:
      0 2px 8px rgba(59, 130, 246, 0.15),
      inset 0 0 0 2px #3b82f6;
  }

  .week-card.future {
    background: #fefefe;
    box-shadow: inset 0 0 0 1px #e9d5ff;
  }

  .week-card.drop-target {
    background: #dbeafe !important;
    box-shadow: inset 0 0 0 2px #3b82f6 !important;
  }

  /* Week header */
  .week-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.5rem;
    border-bottom: 1px solid #f3f4f6;
  }

  .week-card.current .week-header {
    background: #eff6ff;
    border-bottom-color: #bfdbfe;
  }

  .week-title-btn {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.125rem;
    border-radius: 0.25rem;
    transition: background 0.15s;
  }

  .week-title-btn:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  .week-range {
    font-size: 0.75rem;
    font-weight: 700;
    color: #374151;
  }

  .week-number {
    font-size: 0.625rem;
    font-weight: 500;
    color: #9ca3af;
    background: #f3f4f6;
    padding: 0.0625rem 0.25rem;
    border-radius: 0.1875rem;
  }

  .current-badge {
    font-size: 0.5625rem;
    font-weight: 600;
    color: white;
    background: #3b82f6;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
  }

  .expand-toggle-btn {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.5rem;
    font-size: 0.5625rem;
    font-weight: 500;
    color: #6b7280;
    background: none;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    margin-left: auto;
  }

  .expand-toggle-btn:hover {
    color: #3b82f6;
    border-color: #3b82f6;
    background: #eff6ff;
  }

  .expand-toggle-btn.all-expanded {
    color: #3b82f6;
    border-color: #93c5fd;
    background: #eff6ff;
  }

  .toggle-icon {
    font-size: 0.5rem;
    transition: transform 0.15s ease;
  }

  .toggle-text {
    line-height: 1;
  }

  .add-btn {
    width: 1.25rem;
    height: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 500;
    color: #9ca3af;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .add-btn:hover {
    color: #3b82f6;
    background: #dbeafe;
  }

  /* Week content */
  .week-content {
    display: flex;
    padding: 0.375rem 0.5rem;
    gap: 0.5rem;
  }

  .week-card.current .week-content {
    padding: 0.5rem;
  }

  .projects-area {
    flex: 1;
    min-width: 0;
  }

  .projects-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    align-items: flex-start;
    align-content: flex-start;
  }

  .projects-grid.expanded {
    gap: 0.375rem;
  }

  /* Expandable chip sizing within grid - consistent width everywhere */
  .projects-grid :global(.expandable-chip) {
    width: 15rem;
    max-width: 15rem;
    flex-shrink: 0;
  }

  .empty-week {
    font-size: 0.625rem;
    color: #9ca3af;
    padding: 0.25rem;
  }

  /* Events sidebar */
  .events-sidebar {
    width: 15rem;
    height: 11.875rem;
    flex-shrink: 0;
    background: #faf5ff;
    border-radius: 0.25rem;
    padding: 0.375rem;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .events-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.625rem;
    font-weight: 600;
    color: #7c3aed;
    margin-bottom: 0.25rem;
    flex-shrink: 0;
  }

  .events-add-btn {
    width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 500;
    color: #7c3aed;
    background: none;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .events-add-btn:hover {
    color: #7c3aed;
    background: #ede9fe;
    border-color: #7c3aed;
  }

  .events-list {
    display: flex;
    flex-direction: column;
    gap: 0.1875rem;
    overflow-y: auto;
    flex: 1;
    min-height: 0;

    /* Hide scrollbar until hover */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
  }

  .events-list::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }

  .events-list::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 3px;
  }

  .events-list:hover {
    scrollbar-width: thin; /* Firefox */
  }

  .events-list:hover::-webkit-scrollbar-thumb {
    background: #d1d5db;
  }

  .events-list:hover::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }

  .event-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.375rem;
    padding-left: 0.5rem;
    background: #fefefe;
    border: none;
    border-radius: 0.125rem;
    font-size: 0.5625rem;
    flex-shrink: 0;
    min-width: 0;
    overflow: hidden;
    cursor: pointer;
    width: 100%;
    text-align: left;
    transition: background 0.15s;
  }

  .event-item:hover {
    background: #f3f4f6;
  }

  .event-item::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: #9ca3af;
    border-radius: 0.125rem 0 0 0.125rem;
  }

  /* Event type accent colors */
  .event-item.event-appointment::before {
    background: #60a5fa; /* blue-400 */
  }

  .event-item.event-travel::before {
    background: #a78bfa; /* purple-400 */
  }

  .event-item.event-event::before {
    background: #4ade80; /* green-400 */
  }

  .event-item.event-deadline::before {
    background: #f87171; /* red-400 */
  }

  .event-date {
    font-size: 0.5rem;
    font-weight: 500;
    color: #4b5563;
    background: #f3f4f6;
    padding: 0.0625rem 0.25rem;
    border-radius: 0.125rem;
    flex-shrink: 0;
    font-family: ui-monospace, monospace;
    letter-spacing: -0.02em;
  }

  .event-title {
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  .event-span {
    font-size: 0.5rem;
    font-weight: 600;
    color: #9ca3af;
    margin-left: auto;
    flex-shrink: 0;
  }

  .events-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.5625rem;
    color: #9ca3af;
    font-style: italic;
  }
</style>
